import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiClientError } from '@/lib/api/client'
import { fetchMascotGame, sendMascotGameCommand } from './mascot-game-api'
import type { GameAction, GameCommand, MascotGamePayload } from './mascot-game-types'

function errorMessage(error: unknown) {
  return error instanceof ApiClientError
    ? Object.values(error.errors ?? {}).flat()[0] || error.message
    : 'Não foi possível conectar ao seu cantinho. Tente novamente.'
}

type GameToast = { kind: 'success' | 'error'; text: string }
const TOAST_DURATION_MS = 5000

/** The open game owns all reads/writes. No background polling or per-frame HTTP calls. */
export function useMascotGame(
  campaignId: number,
  onCoinsEarned?: (amount: number) => void
) {
  const [data, setData] = useState<MascotGamePayload | null>(null)
  const dataRef = useRef<MascotGamePayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [toast, setToast] = useState<GameToast | null>(null)
  const toastTimer = useRef<number | undefined>(undefined)
  const [uncertain, setUncertain] = useState(false)
  const pending = useRef<GameCommand | null>(null)
  const busyRef = useRef(false)
  const mounted = useRef(false)
  const readController = useRef<AbortController | null>(null)
  const clockOffset = useRef(0)
  const [now, setNow] = useState(() => Date.now() / 1000)

  const clearToast = useCallback(() => {
    window.clearTimeout(toastTimer.current)
    toastTimer.current = undefined
    setToast(null)
  }, [])

  const showToast = useCallback((kind: GameToast['kind'], text: string) => {
    window.clearTimeout(toastTimer.current)
    if (!text) {
      setToast(null)
      return
    }
    setToast({ kind, text })
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = undefined
      setToast(null)
    }, TOAST_DURATION_MS)
  }, [])

  const accept = useCallback(
    (payload: MascotGamePayload) => {
      const previous = dataRef.current
      const earned =
        previous?.campaign_id === payload.campaign_id
          ? payload.state.coins - previous.state.coins
          : 0
      dataRef.current = payload
      clockOffset.current = payload.server_time - Date.now() / 1000
      setNow(payload.server_time)
      setData(payload)
      if (earned > 0) onCoinsEarned?.(earned)
    },
    [onCoinsEarned]
  )

  const refresh = useCallback(async () => {
    if (busyRef.current) return
    readController.current?.abort()
    const controller = new AbortController()
    readController.current = controller
    setLoading(true)
    setError('')
    clearToast()
    try {
      const payload = await fetchMascotGame(campaignId, controller.signal)
      if (!controller.signal.aborted && mounted.current) accept(payload)
    } catch (err) {
      if (!controller.signal.aborted && mounted.current) {
        const text = errorMessage(err)
        setError(text)
        showToast('error', text)
      }
    } finally {
      if (!controller.signal.aborted && mounted.current) setLoading(false)
    }
  }, [accept, campaignId, clearToast, showToast])

  useEffect(() => {
    mounted.current = true
    void refresh()
    const timer = window.setInterval(
      () => setNow(Date.now() / 1000 + clockOffset.current),
      1000
    )
    return () => {
      mounted.current = false
      readController.current?.abort()
      window.clearInterval(timer)
      window.clearTimeout(toastTimer.current)
    }
  }, [refresh])

  const execute = useCallback(
    async (command: GameCommand) => {
      if (busyRef.current) return null
      busyRef.current = true
      readController.current?.abort()
      setBusy(true)
      setError('')
      setMessage('')
      clearToast()
      const retryingUnconfirmed = pending.current?.command_id === command.command_id
      pending.current = command
      try {
        const payload = await sendMascotGameCommand(campaignId, command)
        if (mounted.current) {
          accept(payload)
          setMessage(payload.message)
          showToast('success', payload.message)
          setUncertain(false)
          pending.current = null
        }
        return payload
      } catch (err) {
        if (!mounted.current) return null
        const ambiguous =
          retryingUnconfirmed ||
          !(err instanceof ApiClientError) ||
          err.status === 0 ||
          (err.status >= 200 && err.status < 300) ||
          err.status >= 500
        setUncertain(ambiguous)
        if (!ambiguous) pending.current = null
        const text = errorMessage(err)
        setError(text)
        showToast('error', text)
        if (err instanceof ApiClientError && err.status === 409) {
          try {
            const payload = await fetchMascotGame(campaignId)
            if (mounted.current) accept(payload)
          } catch {
            /* Keep the conflict visible and offer the explicit refresh control. */
          }
        }
        return null
      } finally {
        busyRef.current = false
        if (mounted.current) {
          setBusy(false)
          setLoading(false)
        }
      }
    },
    [accept, campaignId, clearToast, showToast]
  )

  const send = useCallback(
    (action: GameAction) => {
      if (!dataRef.current || busyRef.current || pending.current)
        return Promise.resolve(null)
      return execute({
        ...action,
        revision: dataRef.current.revision,
        command_id: crypto.randomUUID(),
      })
    },
    [execute]
  )

  const retry = () => (pending.current ? execute(pending.current) : refresh())
  return {
    data,
    loading,
    busy,
    error,
    message,
    toast,
    now,
    uncertain,
    send,
    retry,
    refresh,
  }
}
